package com.example.mystybag.core.logic.base

import com.arkivanov.decompose.router.stack.ChildStack
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.pushNew
import com.arkivanov.decompose.value.Value
import com.example.mystybag.core.defines.UtilsActionsDefines.ACTION_NONE
import com.example.mystybag.core.expected.LogHelper

abstract class ParentViewModel(
    context                 : AppContext,
    config                  : NavigatorConfig,
    baseAction              : Int                = ACTION_NONE
): BaseViewModel(context, baseAction) {

    protected var pendingNavigation  : MutableMap<NavigatorConfig, ParentCallback> = mutableMapOf()
    protected val navigation         = StackNavigation<NavigatorConfig>()

    val childStack: Value<ChildStack<NavigatorConfig, BaseViewModel>> by lazy {
        context.appChildStack(
            source                  = navigation,
            initialConfiguration    = config,
            key                     = ::getStackKey,
            navigation              = ::getChildNavigation,
            handleBackButton        = true,
            callbacks               = ::getCallbacks,
            childFactory            = ::createViewModel
        )
    }

    protected val viewModelOnTop: BaseViewModel?
        get() = try {
            childStack.value.active.let {
                (it.instance as? ParentViewModel)?.viewModelOnTop ?: it.instance
            }
        }
        catch (ex: Exception) {
            LogHelper.logExceptionEvent(ex)
            null
        }

    /** Must be unique across all navigation stacks in the app */
    abstract fun getStackKey(): String

    open fun getChildNavigation(): StackNavigation<NavigatorConfig> = navigation
    open fun getCallbacks(config: NavigatorConfig): ParentCallback? = this

    /** Override in successor to create all possible ViewModels, launched from this parent */
    @Throws(UnsupportedOperationException::class)
    open fun createViewModel(config: NavigatorConfig, context: AppContext): BaseViewModel {
        throw UnsupportedOperationException("${getStackKey()} -> attempt to open not allowed config: [ $config ]")
    }

    override fun navigate(config: NavigatorConfig) = navigation.pushNew(config)

    open fun finish(onComplete: ((Boolean) -> Unit)? = null) {
        setResult<ParentCallback> { goBack(onComplete) }
    }

    override fun goBack(onComplete: ((Boolean) -> Unit)?) {
        if (childStack.value.backStack.isEmpty())
            finish(onComplete)
        else
            navigation.pop {
                onComplete?.invoke(it)
            }
    }

    override fun parentCloseBottomSheet() {
        closeBottomSheet()
    }

    override fun parentOpenBottomSheet(config: UIBottomSheetConfig) {
        openBottomSheet(config)
    }

    @Suppress("unused", "UNNECESSARY_SAFE_CALL")
    fun dumpStack(level: Int = 1) {
        println("StackDump::${"\t".repeat(level)} * ${getStackKey()}")
        childStack?.value?.items?.forEach { // Sometimes this may be null
            if (it.instance is ParentViewModel)
                (it.instance as ParentViewModel).dumpStack(level + 1)
            else
                println("StackDump::${"\t".repeat(level)} - ${it.instance}\n")
        }
    }
}